// src/components/GearForm.jsx

import React, { useEffect, useState } from "react";

export default function GearForm({
  gear,
  onClose,
  onSave,
  team = [],
  projects = [],
  categories = [],
  statuses = [],
  fields = [],
  isAdminOrManager = false,
  asPage = false,
  showTitle = true,
}) {
  const buildInitialValues = (g) => {
    const obj = {};

    // Initialize all dynamic fields
    fields.forEach((f) => {
      obj[f.name] = g?.[f.name] ?? "";
    });

    // Ensure imageUrl always exists even if it's not part of fields/defaultFields
    if (typeof obj.imageUrl === "undefined") {
      obj.imageUrl = g?.imageUrl ?? "";
    }

    return obj;
  };

  const [values, setValues] = useState(() => buildInitialValues(gear));
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Keep form in sync when switching between add/edit or when fields change
  useEffect(() => {
    setValues(buildInitialValues(gear));
    setSubmitError("");
    setImageError("");
  }, [gear, fields]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((v) => ({
      ...v,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageChange = async (e) => {
    setImageError("");
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setImageError("File must be an image");
      return;
    }

    setImageUploading(true);
    try {
      // No Firebase: local preview URL
      const localUrl = URL.createObjectURL(file);
      setValues((v) => ({ ...v, imageUrl: localUrl }));
    } catch (err) {
      setImageError("Upload failed");
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitting(true);

    const final = {
      ...values,
      dateAdded: gear?.dateAdded || new Date().toISOString(),
    };

    try {
      if (typeof onSave !== "function") {
        throw new Error("Save handler is missing (onSave not provided).");
      }
      await Promise.resolve(onSave(final, gear?.id));
    } catch (err) {
      setSubmitError(err?.response?.data?.error || err?.message || "Server error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {showTitle && (
        <h3 className={asPage ? "font-semibold text-xl mb-4" : "font-semibold text-xl mb-4"}>
          {gear ? "Edit Gear" : "Add Equipment"}
        </h3>
      )}

      <form
        onSubmit={handleSubmit}
        className="space-y-4 overflow-y-auto"
        style={{ maxHeight: asPage ? "unset" : "65vh" }}
      >
        {/* IMAGE UPLOAD */}
        <div className="mb-2 flex items-center gap-4">
          <img
            src={values.imageUrl || "/gear-placeholder.png"}
            alt="Gear preview"
            className="w-16 h-16 object-cover rounded-full bg-gray-100 border"
          />
          <div className="flex flex-col">
            <label className="text-sm font-medium">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={imageUploading}
              className="mt-1"
            />
            {imageUploading && (
              <div className="text-xs text-blue-500">Uploading...</div>
            )}
            {imageError && (
              <div className="text-xs text-red-600">{imageError}</div>
            )}
          </div>
        </div>

        {/* FIELDS */}
        {fields.map((f) => (
          <div key={f.name}>
            <label className="block mb-1 font-medium">
              {f.label}
              {f.required && <span className="text-red-600 ml-1">*</span>}
            </label>

            {f.type === "text" && (
              <input
                name={f.name}
                value={values[f.name]}
                onChange={handleChange}
                required={f.required}
                className="border rounded w-full px-3 py-2"
              />
            )}

            {f.type === "textarea" && (
              <textarea
                name={f.name}
                value={values[f.name]}
                onChange={handleChange}
                required={f.required}
                className="border rounded w-full px-3 py-2"
              />
            )}

            {f.type === "select" && (
              <select
                name={f.name}
                value={values[f.name]}
                onChange={handleChange}
                required={f.required}
                className="border rounded w-full px-3 py-2"
              >
                <option value="">Select...</option>

                {f.name === "category" &&
                  categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}

                {f.name === "status" &&
                  statuses.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}

                {f.name === "assignedTo" &&
                  team.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}

                {f.name === "assignedProject" &&
                  projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
            )}

            {f.type === "date" && (
              <input
                name={f.name}
                type="date"
                value={values[f.name]}
                onChange={handleChange}
                required={f.required}
                className="border rounded w-full px-3 py-2"
              />
            )}

            {f.type === "number" && (
              <input
                name={f.name}
                type="number"
                value={values[f.name]}
                onChange={handleChange}
                required={f.required}
                className="border rounded w-full px-3 py-2"
              />
            )}

            {f.type === "checkbox" && (
              <input
                name={f.name}
                type="checkbox"
                checked={!!values[f.name]}
                onChange={handleChange}
                className="mr-2"
              />
            )}
          </div>
        ))}

        {submitError && (
          <div className="text-xs text-red-600">{submitError}</div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
            disabled={submitting}
          >
            Cancel
          </button>

          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            disabled={imageUploading || submitting}
          >
            {submitting ? "Saving..." : gear ? "Save" : "Add"}
          </button>
        </div>
      </form>
    </div>
  );
}
